import { useState, useEffect, useRef } from 'react';
import { FormProvider, useForm, useFieldArray } from 'react-hook-form';
import ChartBottomBtn from 'components/pages/chart/ChartBottomBtn';
import ConfirmChartRegister from 'pages/modal/ConfirmChartRegister';
import DHImplementationChart from '../DHImplementationChart';
import {
  assessmentLists,
  DHImplementationChartFormDefaultValues,
  AttachmentFilesDefaultValues,
} from 'constants/chart/dhImplementationChart';
import {
  TDHImplementationChartFormValues,
  TAttachmentFiles,
} from 'types/chart/dhImplementationChart';
import { TSelectObject } from 'types/dropdown';
import { TPracticeChartInfo } from 'types/apis/chart/studentPracticeChart';
import {
  TEvaluationChartInfo,
  TPutEvaluationChartResponse,
} from 'types/apis/chart/studentEvaluationChart';
import { TChartFileInfoList } from 'types/chart';
import useModal from 'hooks/useModal';
import { TChartInfoResponse } from 'hooks/chart/useGetChartInfo';
import useChartRegister from 'hooks/chart/useChartRegister';

type TProps = {
  query: URLSearchParams;
  chartType: number;
  category: string;
  id: number;
  isRegister: boolean;
  setSubmitEndDate: (submitEndDate: string) => void;
  chartInfoResponse: TChartInfoResponse | null;
  setChartInfoResponse: (data: any) => void;
  activeTabIndex: number;
  isLoading: boolean;
  setIsLoading: (isLoading: boolean) => void;
  isEdit: boolean;
  isReadOnly: boolean;
  checkSubmitEndDate: (submitEndDate: string) => void;
  handleSuccessGetChartInfo: (data: TChartInfoResponse) => void;
};

const DHImplementationChartRegister = ({
  query,
  chartType,
  category,
  id,
  isRegister,
  setSubmitEndDate,
  chartInfoResponse,
  setChartInfoResponse,
  activeTabIndex,
  isLoading,
  setIsLoading,
  isEdit,
  isReadOnly,
  checkSubmitEndDate,
  handleSuccessGetChartInfo,
}: TProps) => {
  const { openModal } = useModal();

  const [date, setDate] = useState<Date[]>([new Date()]);
  const [showAssessmentList, setShowAssessmentList] = useState<boolean[]>([
    false,
  ]);
  const [selectedObject, setSelectedObject] = useState<TSelectObject[]>([]);
  const [attachmentFiles, setAttachmentFiles] = useState<TAttachmentFiles>(
    AttachmentFilesDefaultValues
  );
  const [deleteFileIdList, setDeleteFileIdList] = useState<number[]>([]);

  const { chartRegister } = useChartRegister(({ data }) => afterAutoSave(data));

  const methods = useForm<TDHImplementationChartFormValues>({
    defaultValues: {
      field: [DHImplementationChartFormDefaultValues],
    },
  });
  const { handleSubmit, control, watch, reset } = methods;
  const formValues = watch();
  const formValuesRef = useRef(formValues);
  const deleteFileIdListRef = useRef(deleteFileIdList);
  const attachmentFilesRef = useRef(attachmentFiles);
  const prevFormValuesRef = useRef<TDHImplementationChartFormValues | null>(
    null
  );
  const prevAttachmentFilesRef = useRef<TAttachmentFiles | null>(null);

  const { fields, append, update, remove } = useFieldArray({
    control,
    name: 'field',
  });

  // 실습용 - 자동 저장 (20초 간격)
  useEffect(() => {
    if (isLoading) return;
    prevFormValuesRef.current = JSON.parse(JSON.stringify(formValues));
    prevAttachmentFilesRef.current = attachmentFiles;
  }, [isLoading]);

  useEffect(() => {
    formValuesRef.current = formValues;
    deleteFileIdListRef.current = deleteFileIdList;
    attachmentFilesRef.current = attachmentFiles;
  }, [formValues, deleteFileIdList, attachmentFiles]);

  useEffect(() => {
    let intervalId: NodeJS.Timeout | null = null;
    if (!isLoading && isRegister && category === 'evaluation') {
      intervalId = setInterval(() => {
        if (isReadOnly) return;
        // 초기 상태 자동 저장 방지
        if (
          (chartInfoResponse?.chartInfo as TEvaluationChartInfo[])[
            activeTabIndex > 1 ? 1 : 0
          ]?.chartData === null &&
          JSON.stringify({
            field: [DHImplementationChartFormDefaultValues],
          }) === JSON.stringify(formValuesRef.current)
        )
          return;
        // 이전 값과 비교하여 변경된 경우만 자동 저장 요청
        if (
          JSON.stringify(formValuesRef.current) !==
            JSON.stringify(prevFormValuesRef.current) ||
          JSON.stringify(attachmentFilesRef.current) !==
            JSON.stringify(prevAttachmentFilesRef.current)
        ) {
          onValid(
            true,
            formValuesRef.current,
            deleteFileIdListRef.current,
            attachmentFilesRef.current
          );
        }
      }, 20000);
    }

    return () => {
      if (intervalId) {
        clearInterval(intervalId);
      }
    };
  }, [isRegister, isLoading, category, isReadOnly]);

  const afterAutoSave = ({ chartInfo }: TPutEvaluationChartResponse) => {
    setChartInfoResponse((prev: any) => {
      if (!prev) return prev;
      const newChartInfo = prev.chartInfo as TEvaluationChartInfo[];
      const newChartInfoData = [...newChartInfo];
      newChartInfoData[activeTabIndex > 1 ? 1 : 0] = {
        ...newChartInfoData[activeTabIndex > 1 ? 1 : 0],
        chartData: chartInfo?.chartData || null,
        fileInfoList: chartInfo?.fileInfoList || [],
      };
      return {
        ...prev,
        chartInfo: newChartInfoData,
      };
    });
    prevFormValuesRef.current = chartInfo?.chartData || null;
    setDeleteFileIdList([]);
    setFiles(chartInfo?.fileInfoList || [], true);
  };

  const setChartInfo = (
    chartInfo: TPracticeChartInfo | TEvaluationChartInfo
  ) => {
    const chartData: TDHImplementationChartFormValues | null =
      (chartInfo?.chartData as TDHImplementationChartFormValues | null) || null;

    if (category !== 'practice') {
      const submitEndDate =
        (chartInfo as TEvaluationChartInfo)?.submitEndDate || '';
      setSubmitEndDate(submitEndDate);
      checkSubmitEndDate(submitEndDate);
    }
    const fieldData = chartData?.field || [];
    fields.forEach((_, index) => remove(index));
    if (fieldData.length === 0) {
      reset();
      setFiles([], false);
      setIsLoading(false);
      return;
    }
    fieldData.forEach((data, index) => {
      update(index, data);
      setDate((prev) => {
        const newDate = [...prev];
        newDate[index] = new Date(data.date || new Date());
        return newDate;
      });
      setSelectedObject((prev) => {
        const newSelectedObject = { ...prev };
        const selectedAssessment = assessmentLists.find(
          (list) => list.value === data.assessment
        );
        if (selectedAssessment) {
          newSelectedObject[index] = selectedAssessment;
        }
        return newSelectedObject;
      });
    });

    setFiles(chartInfo?.fileInfoList || [], false);
    setIsLoading(false);
  };

  const setFiles = (
    fileInfoList: TChartFileInfoList[],
    isAutoSave: boolean
  ) => {
    const newAttachmentFiles: TAttachmentFiles = {
      0: null,
    };
    fileInfoList.forEach((file) => {
      if (!newAttachmentFiles[file.fileArrangeNum - 1]) {
        newAttachmentFiles[file.fileArrangeNum - 1] = [];
      }
      newAttachmentFiles[file.fileArrangeNum - 1]?.push(
        new File([], file.filePath, {
          type: `fileid:${file.fileId}`,
        })
      );
    });
    setAttachmentFiles(newAttachmentFiles);
    if (isAutoSave) prevAttachmentFilesRef.current = newAttachmentFiles;
  };

  const changeFile = (index: number, file: File[]) =>
    setAttachmentFiles((prev) => {
      const newFiles = { ...prev };
      newFiles[index] = file;
      return newFiles;
    });

  // 차트 조회
  const formMediaFileRef = useRef<{ resetFile: () => void }>(null);

  useEffect(() => {
    if (!chartInfoResponse) return;
    const chartInfo =
      category === 'practice'
        ? (chartInfoResponse.chartInfo as TPracticeChartInfo)
        : (chartInfoResponse.chartInfo as TEvaluationChartInfo[])[
            activeTabIndex > 1 ? 1 : 0
          ];
    setIsLoading(true);
    if (formMediaFileRef.current) {
      formMediaFileRef.current.resetFile();
    }
    setChartInfo(chartInfo);
  }, [activeTabIndex, chartInfoResponse]);

  useEffect(() => {
    if (chartInfoResponse) {
      handleSuccessGetChartInfo(chartInfoResponse);
    }
  }, []);

  // 차트 등록
  const onValid = (
    isAutoSave: boolean,
    data: TDHImplementationChartFormValues,
    deleteFileIdListData: number[] = deleteFileIdList,
    attachmentFilesData: TAttachmentFiles = attachmentFiles
  ) => {
    const formData = new FormData();

    formData.append(
      'request',
      new Blob(
        [
          JSON.stringify({
            chartType,
            chartData: data,
            deleteFileIdList: deleteFileIdListData,
          }),
        ],
        {
          type: 'application/json',
        }
      )
    );
    data.field.forEach((_, i) => {
      const files = attachmentFilesData[i];

      if (files && files.length > 0) {
        Array.from(files).forEach((file) => {
          if (!file.type.includes('fileid')) {
            formData.append(`fileList${i + 1}`, file);
          }
        });
      }
    });
    chartRegister({
      category,
      id: id,
      data: formData,
      isEdit: isEdit,
      isAutoSave,
      patientId: query.get('patientId') || undefined,
      courseInfoId: query.get('courseInfoId') || undefined,
      chartRound: query.get('chartRound') || undefined,
      onLoading: setIsLoading,
      onConfirm: () => {
        openModal({
          element: (
            <ConfirmChartRegister
              category={category}
              chartType={chartType}
              id={id}
              patientId={query.get('patientId') || undefined}
              courseInfoId={query.get('courseInfoId') || undefined}
              chartRound={query.get('chartRound') || undefined}
              chartName={chartInfoResponse?.chartName}
              data={formData}
            />
          ),
        });
      },
    });
  };

  return (
    <>
      <FormProvider {...methods}>
        <form onSubmit={handleSubmit((data) => onValid(false, data))}>
          <DHImplementationChart
            formMediaFileRef={formMediaFileRef}
            attachmentFiles={attachmentFiles}
            setAttachmentFiles={setAttachmentFiles}
            deleteFileIdList={deleteFileIdList}
            setDeleteFileIdList={setDeleteFileIdList}
            changeFile={changeFile}
            showAssessmentList={showAssessmentList}
            setShowAssessmentList={setShowAssessmentList}
            date={date}
            setDate={setDate}
            selectedObject={selectedObject}
            setSelectedObject={setSelectedObject}
            isRegister={isRegister}
            isReadOnly={isReadOnly}
            fields={fields}
            append={append}
            remove={remove}
          />

          {(Number(query.get('tab')) === 0 ||
            Number(query.get('tab')) === 2) && (
            <ChartBottomBtn
              category={category}
              id={id}
              courseId={query.get('courseId') || undefined}
              patientId={query.get('patientId') || undefined}
              courseInfoId={query.get('courseInfoId') || undefined}
              chartRound={query.get('chartRound') || undefined}
              studentUserId={query.get('studentUserId') || undefined}
              isRegister={isRegister}
              isEdit={isEdit}
              isReadOnly={isReadOnly}
              isChart={true}
              chartName={chartInfoResponse?.chartName}
            />
          )}
        </form>
      </FormProvider>
    </>
  );
};

export default DHImplementationChartRegister;
